import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AgGrid,
  Banner,
  Button,
  Grid,
  ModalV2,
  RadioBar,
  Select,
  TextInput,
} from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { useFeatureFlag } from "@jsq/flags-react";
import { globalAttrFlavorLabel } from "#common/attributeFlavors";
import { sortGlobalsByRolePriority } from "#common/attributeKeys";
import {
  attributeConflictLocation,
  attributeNameConflictMessage,
  findGlobalAttributeNameConflict,
  mintAttributeSlug,
  newAttributeSlugSuffix,
  slugifyAttributeName,
} from "#common/attributeNameConflicts";
import {
  DUPLICATE_VALUE_MESSAGE,
  findDuplicateValueIndexes,
  SAVED_VALUE_SET_LOCK_MESSAGE,
} from "#common/attributeValues";
import { ReadOnlyChip } from "#common/components/ReadOnlyChip";
import { useAttributeNameConflict } from "#common/hooks/useAttributeNameConflict";
import { useGlobalAttributeNameConflict } from "#common/hooks/useGlobalAttributeNameConflict";
import { EDITABLE_DATATYPES } from "./constants";
import { attributeNameConflictErrorMessage } from "./errors";
import { PromoteLocalModal } from "./PromoteLocalModal";
import { SpaceLocalAttributesGrid } from "./SpaceLocalAttributesGrid";
import * as styles from "./styles_vanilla.css";
import { domainName, isSelectType, spaceFromLocalRow } from "./utils";
import type {
  AttrDataType,
  GlobalAttrFlavor,
  GlobalAttribute,
  Space,
  SpaceLocalAttribute,
} from "#common/types/vault";
import type { LocalRow } from "./SpaceLocalAttributesGrid";
import type { DocumentTypeSettingsProps, SettingsTab } from "./types";

export type { DocumentTypeSettingsProps };

type SpaceRow = { id: string; name: string; domain: string; domId: string; attrs: string };
type GlobalRow = {
  key: string;
  name: string;
  flavor: GlobalAttrFlavor;
  dataType: string;
  captures: string;
  valueSet: string;
  usage: string;
};
/* ---------------- Cell renderers ---------------- */
/**
 * What the Source column's filter matches on.
 *
 * The cell renders a LABEL while the row carries the raw flavor ("eng"/"user"),
 * so without this the filter matched neither "Eng-validated" nor "User-created"
 * and typing what the cell displays filtered every row out (DV-439). Resolving
 * through the same mapping the cell uses is what keeps the two from drifting.
 */
export const sourceFilterValue = (row?: { flavor: GlobalAttrFlavor }): string =>
  row ? globalAttrFlavorLabel(row.flavor) : "";

function SourceCell({ value }: { value?: GlobalAttrFlavor }) {
  const flavor = value ?? "eng";
  const user = flavor === "user";
  return (
    <span className={styles.sourceCell}>
      <Icon
        type={user ? "list-alt" : "shield-alt"}
        size="small"
        customColor={user ? "#72716e" : "#118462"}
      />
      {globalAttrFlavorLabel(flavor)}
    </span>
  );
}

/* ---------------- Value-set editor ---------------- */
function ValueSetEditor({
  values,
  onChange,
  duplicateIndexes = new Set(),
  disabled = false,
  lockedCount = 0,
}: {
  values: string[];
  onChange: (v: string[]) => void;
  duplicateIndexes?: Set<number>;
  /** Locks every affordance — edit, remove, and add — leaving the set visible. */
  disabled?: boolean;
  /** Leading values already saved: read-only, with no remove control. New values
      append, so a locked index never shifts under an edit. */
  lockedCount?: number;
}) {
  // The list scrolls past five rows, so an appended row can land out of sight.
  const [rowToFocus, setRowToFocus] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (rowToFocus === null) return;
    listRef.current?.querySelectorAll("input")[rowToFocus]?.focus();
    setRowToFocus(null);
  }, [rowToFocus]);

  return (
    <div className={styles.valset}>
      {values.length === 0 ? (
        <div className={styles.valsetEmpty}>No values yet — add each allowed value below.</div>
      ) : (
        <div className={styles.valsetList} ref={listRef}>
          {values.map((v, i) => {
            const saved = i < lockedCount;
            return (
              <React.Fragment key={i}>
                <div className={styles.valsetInput}>
                  <TextInput
                    value={v}
                    placeholder={`Value ${i + 1}`}
                    errorMessage={duplicateIndexes.has(i) ? "Duplicate value" : undefined}
                    disabled={disabled || saved}
                    onChange={(e) =>
                      onChange(values.map((x, j) => (j === i ? e.target.value : x)))
                    }
                  />
                </div>
                {/* The grid places two children per row, so the action cell has to
                    exist even when a saved value offers nothing to click. */}
                {saved ? (
                  <span />
                ) : (
                  <Button
                    variant="muted"
                    icon="xmark"
                    aria-label="Remove value"
                    disabled={disabled}
                    onClick={() => onChange(values.filter((_, j) => j !== i))}
                  >
                    {""}
                  </Button>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}
      <div>
        <Button
          variant="neutral"
          icon="plus"
          iconPosition="left"
          disabled={disabled}
          onClick={() => {
            setRowToFocus(values.length);
            onChange([...values, ""]);
          }}
        >
          Add value
        </Button>
      </div>
    </div>
  );
}

/* ---------------- Global attribute create/edit modal ---------------- */
function GlobalAttrModal({
  attr,
  globalLib,
  spaces,
  onSave,
  onCreate,
  onClose,
}: {
  attr: GlobalAttribute | null;
  globalLib: GlobalAttribute[];
  /** Every space: a global may not shadow any space's local attribute name. */
  spaces: Space[];
  onSave?: (key: string, patch: Partial<GlobalAttribute>) => void | Promise<void>;
  onCreate?: (attr: GlobalAttribute) => void | Promise<void>;
  onClose: () => void;
}) {
  const isEdit = !!attr;
  const flavor = attr?.flavor ?? "user";
  const isEng = flavor === "eng";
  // `update_global_attribute` rejects eng-validated globals (FORBIDDEN_SYSTEM_BOUND),
  // so the form is fully read-only for them rather than letting the user submit a refused edit.
  const locked = isEdit && isEng;
  /**
   * `valueType` is fixed at creation for every global, not just eng-validated
   * ones: documents already tagged store their values in its shape, so
   * `update_global_attribute` refuses the change. The value set stays editable.
   */
  const dataTypeLocked = isEdit;
  const [name, setName] = useState(attr?.name ?? "");
  const [dataType, setDataType] = useState<AttrDataType>(attr?.dataType ?? "Single-select");
  const [captures, setCaptures] = useState(attr?.captures ?? "");
  const [values, setValues] = useState<string[]>(attr?.values ? [...attr.values] : []);
  /* Frozen at mount, not derived from `values`: the lock must not move as the
     set is extended. */
  const [savedValueCount] = useState(attr?.values?.length ?? 0);
  const [suffix] = useState(newAttributeSlugSuffix);
  const [submitting, setSubmitting] = useState(false);
  // A name the SERVER refused, which the client pre-check had cleared (it cannot
  // see ACL-filtered locals, and another steward may have taken the name since).
  // Cleared on the next keystroke: it describes the name that was submitted, not
  // whatever is in the field now.
  const [writeError, setWriteError] = useState<string | null>(null);
  const slug = isEdit ? attr.key : mintAttributeSlug(name, suffix);
  const selectType = isSelectType(dataType);
  const needsValues = !locked && selectType;
  const clean = values.map((v) => v.trim()).filter(Boolean);
  const duplicateValueIndexes = selectType
    ? findDuplicateValueIndexes(values)
    : new Set<number>();
  const hasDuplicateValues = duplicateValueIndexes.size > 0;
  // A global lands in every space, so it may not take the name of a local in any of
  // them, nor of another global.
  const { conflict: nameConflict, isChecking: isCheckingName } = useGlobalAttributeNameConflict(
    {
      name,
      slug: isEdit ? undefined : slug,
      globalAttributes: globalLib,
      spaces,
      ignoreGlobalKey: attr?.key,
    },
  );
  const ok =
    name.trim() !== "" &&
    !nameConflict &&
    !isCheckingName &&
    (!needsValues || clean.length > 0) &&
    !hasDuplicateValues;

  const submit = async () => {
    // Defence in depth: the Save button is disabled for an eng-validated
    // attribute, but no code path may emit an update the backend would reject.
    if (locked || !ok || submitting) return;
    const patch: Partial<GlobalAttribute> = {
      name: name.trim(),
      dataType,
      flavor,
      captures: captures.trim() || "User-created global attribute.",
      validates: isEng ? (attr?.validates ?? "Workspace data") : null,
      values: selectType ? clean : null,
    };
    setSubmitting(true);
    try {
      const result = isEdit
        ? onSave?.(attr.key, patch)
        : onCreate?.({
            // `ok` gates on a non-empty name, so `slug` is exactly what the field showed.
            key: slug,
            ...(patch as Omit<GlobalAttribute, "key">),
          });
      if (result instanceof Promise) await result;
      onClose();
    } catch (error) {
      // A refused NAME belongs under the name field, not in a toast (DV-511):
      // it is a correction the steward makes right here. Anything else is left
      // to the container — which reports it on the EDIT path
      // (`onSaveGlobalAttr` toasts and re-throws) but not yet on CREATE, where
      // `onAddGlobalAttr` has no error toast, so a non-name create failure is
      // still silent. That gap predates DV-511 and is left alone here rather
      // than widened. Either way the modal stays open, so the work survives.
      setWriteError(attributeNameConflictErrorMessage(name.trim(), error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalV2
      show
      onHide={onClose}
      size="medium"
      contentClassName={styles.modalPanel}
      title={isEdit ? "Edit global attribute" : "New global attribute"}
    >
      <ModalV2.Body className={styles.modalBody}>
        <div className={isEng ? styles.flavorNote.eng : styles.flavorNote.user}>
          <Icon type={isEng ? "badge-check" : "list-alt"} size="small" />
          <span>
            {isEng ? (
              <>
                <b>{globalAttrFlavorLabel(flavor)}</b> — values are checked against a source of
                truth maintained by engineering.
              </>
            ) : (
              <>
                <b>{globalAttrFlavorLabel(flavor)}</b> — not validated against a source of
                truth. Define a discrete list of allowed values below.
              </>
            )}
            {locked && " This attribute is read-only and cannot be edited here."}
          </span>
        </div>
        <div className={styles.field}>
          <TextInput
            label={<>Display name {locked && <ReadOnlyChip />}</>}
            value={name}
            placeholder="e.g. Confidentiality"
            isFullWidth
            // Frozen while the write is in flight. The refusal that comes back
            // names the text that was SUBMITTED, so letting the field move
            // underneath it would render an error against a name nobody sent —
            // the `onChange` clear below cannot help, since the rejection lands
            // after it. Locking the field is what makes the two agree; a
            // "discard if the name changed" guard would instead swallow the
            // failure silently, which is the thing DV-511 exists to stop.
            disabled={locked || submitting}
            onChange={(e) => {
              setName(e.target.value);
              // The refusal named the submitted text; it says nothing about this one.
              setWriteError(null);
            }}
          />
          {/* One error slot, two sources. The client pre-check wins when it has a
              verdict — it can name WHERE the clash lives, which the server's
              reply cannot. */}
          {nameConflict ? (
            <div className={styles.fieldError} role="alert">
              {attributeNameConflictMessage(nameConflict)}
            </div>
          ) : (
            writeError && (
              <div className={styles.fieldError} role="alert">
                {writeError}
              </div>
            )
          )}
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>
            Attribute slug {isEdit ? <ReadOnlyChip /> : "· generated from the display name"}
          </span>
          <input
            className={styles.slugInput}
            value={slug}
            placeholder="set_on_creation"
            readOnly
            disabled
          />
          <div className={styles.help}>
            The slug is fixed once created — reporting and integrations reference it, so it
            never changes even when you rename the attribute.
            {!isEdit && " A short random suffix keeps it unique across the workspace."}
          </div>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>
            Data type {dataTypeLocked && <ReadOnlyChip />}
          </span>
          <Select
            value={dataType}
            full
            disabled={dataTypeLocked}
            onChange={(v) => setDataType(v as AttrDataType)}
          >
            <Select.Section>
              {(EDITABLE_DATATYPES.includes(dataType)
                ? EDITABLE_DATATYPES
                : [dataType, ...EDITABLE_DATATYPES]
              ).map((t) => (
                <Select.Option key={t} value={t}>
                  {t}
                </Select.Option>
              ))}
            </Select.Section>
          </Select>
          {isEdit && (
            <div className={styles.help}>
              The data type is fixed once created — documents already tagged with this attribute
              store their values in its shape.
            </div>
          )}
        </div>
        {selectType && (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>
              Value set{needsValues ? " · required" : ""} {locked && <ReadOnlyChip />}
            </span>
            <ValueSetEditor
              values={values}
              onChange={setValues}
              duplicateIndexes={duplicateValueIndexes}
              disabled={locked}
              lockedCount={savedValueCount}
            />
            {hasDuplicateValues && (
              <div className={styles.fieldError} role="alert">
                {DUPLICATE_VALUE_MESSAGE}
              </div>
            )}
          </div>
        )}
        <div className={styles.field}>
          <TextInput
            label={<>Captures {locked && <ReadOnlyChip />}</>}
            value={captures}
            placeholder="What this attribute records"
            isFullWidth
            disabled={locked}
            onChange={(e) => setCaptures(e.target.value)}
          />
        </div>
      </ModalV2.Body>
      <ModalV2.Footer>
        <div className={styles.modalFooter}>
          <Button variant="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={isEdit ? "check" : "plus"}
            iconPosition="left"
            disabled={locked || !ok || submitting}
            title={
              locked
                ? "Eng-validated attributes are maintained by engineering and cannot be edited."
                : undefined
            }
            onClick={() => void submit()}
          >
            {isEdit ? "Save changes" : "Create global attribute"}
          </Button>
        </div>
      </ModalV2.Footer>
    </ModalV2>
  );
}

/* ---------------- Space-local edit modal (edit / delete / promote) ---------------- */
function LocalEditModal({
  space,
  local,
  globalLib,
  onDeletionImpact,
  onSave,
  onDelete,
  onClose,
}: {
  space: Space;
  local: SpaceLocalAttribute;
  globalLib: GlobalAttribute[];
  onDeletionImpact?: (attributeId: string) => Promise<number | null>;
  onSave?: DocumentTypeSettingsProps["onSaveLocal"];
  onDelete?: (spaceId: string, localId: string, name: string) => void | Promise<void>;
  onClose: () => void;
}) {
  const [name, setName] = useState(local.name);
  const [dataType, setDataType] = useState<AttrDataType>(local.dataType);
  const [required, setRequired] = useState(!!local.required);
  const [options, setOptions] = useState<string[]>(local.options ? [...local.options] : []);
  /* Frozen at mount, not derived from `options`: the lock must not move as the
     set is extended. */
  const [savedOptionCount] = useState(local.options?.length ?? 0);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteImpact, setDeleteImpact] = useState<
    | { status: "idle" | "loading" | "unavailable" | "not_found" }
    | { status: "ready"; count: number }
  >({ status: "idle" });
  const deleteImpactRequestId = useRef(0);
  const [deleting, setDeleting] = useState(false);
  const [deleteFailed, setDeleteFailed] = useState(false);
  const selectType = isSelectType(dataType);
  // The persisted slug, not one re-minted from the display name: a slug carries a
  // random uniqueness tail and survives renames, so re-slugifying shows a value
  // that is neither what reporting references nor what the backend stores.
  const slug = local.slug ?? slugifyAttributeName(local.name);
  const clean = options.map((o) => o.trim()).filter(Boolean);
  const duplicateOptionIndexes = selectType
    ? findDuplicateValueIndexes(options)
    : new Set<number>();
  const hasDuplicateOptions = duplicateOptionIndexes.size > 0;
  // A rename can collide just as creating one in the space editor can. The slug is
  // read-only here, so only the display name is checked.
  const { conflict: nameConflict, isChecking: isCheckingName } = useAttributeNameConflict({
    name,
    globalAttributes: globalLib,
    localAttributes: space.locals,
    ignoreLocalId: local.id,
  });
  const ok =
    name.trim() !== "" &&
    !nameConflict &&
    !isCheckingName &&
    (!selectType || clean.length > 0) &&
    !hasDuplicateOptions;

  const submit = async () => {
    if (!ok || submitting) return;
    setSubmitting(true);
    try {
      const result = onSave?.(
        space.id,
        local.id,
        {
          name: name.trim(),
          dataType,
          required,
          // Defaults are not persisted for space-locals (no backend field).
          default: "",
          options: selectType ? clean : [],
        },
        local,
      );
      if (result instanceof Promise) await result;
      onClose();
    } catch {
      // The container reports the refusal (it owns the toast). Staying open is
      // what this catch is for: the admin's edits survive a failed save.
    } finally {
      setSubmitting(false);
    }
  };

  const loadDeleteImpact = async () => {
    const requestId = ++deleteImpactRequestId.current;
    setDeleteImpact({ status: "loading" });
    setDeleteFailed(false);
    if (!onDeletionImpact) {
      if (requestId === deleteImpactRequestId.current) {
        setDeleteImpact({ status: "unavailable" });
      }
      return;
    }
    try {
      const count = await onDeletionImpact(local.id);
      if (requestId !== deleteImpactRequestId.current) return;
      setDeleteImpact(count === null ? { status: "not_found" } : { status: "ready", count });
    } catch {
      if (requestId !== deleteImpactRequestId.current) return;
      setDeleteImpact({ status: "unavailable" });
    }
  };

  const closeDeleteConfirmation = () => {
    deleteImpactRequestId.current += 1;
    setConfirmDelete(false);
  };

  const openDeleteConfirmation = () => {
    setConfirmDelete(true);
    void loadDeleteImpact();
  };

  const confirmDeletion = async () => {
    setDeleting(true);
    setDeleteFailed(false);
    try {
      await onDelete?.(space.id, local.id, local.name);
      onClose();
    } catch {
      setDeleteFailed(true);
      setDeleting(false);
    }
  };

  return (
    <>
      <ModalV2
        show
        onHide={onClose}
        size="medium"
        closeOnClickOutside={false}
        title="Edit space-local attribute"
      >
        <ModalV2.Body>
          <div className={styles.localNote}>
            <span className={styles.localNoteIcon}>
              <Icon type="pen-to-square" size="small" />
            </span>
            <div>
              <b>Space-local</b> — sandboxed to <b>{space.name}</b>. Edits here apply only to
              this space.
            </div>
          </div>
          <div className={styles.field}>
            <TextInput
              label="Display name"
              value={name}
              isFullWidth
              onChange={(e) => setName(e.target.value)}
            />
            {nameConflict && (
              <div className={styles.fieldError} role="alert">
                {attributeNameConflictMessage(nameConflict)}
              </div>
            )}
          </div>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>
              Attribute slug <ReadOnlyChip />
            </span>
            <input className={styles.slugInput} value={slug} readOnly disabled />
            <div className={styles.help}>
              The slug is fixed once created — reporting and integrations reference it, so it
              never changes even when you rename the attribute.
            </div>
          </div>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>
              Data type <ReadOnlyChip />
            </span>
            <Select
              value={dataType}
              full
              disabled
              onChange={(v) => {
                setDataType(v as AttrDataType);
                setOptions([]);
              }}
            >
              <Select.Section>
                {(EDITABLE_DATATYPES.includes(dataType)
                  ? EDITABLE_DATATYPES
                  : [dataType, ...EDITABLE_DATATYPES]
                ).map((t) => (
                  <Select.Option key={t} value={t}>
                    {t}
                  </Select.Option>
                ))}
              </Select.Section>
            </Select>
          </div>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Requirement</span>
            <div className={styles.reqSeg}>
              <RadioBar
                value={required ? "required" : "optional"}
                onSelectOption={(v) => setRequired(v === "required")}
                options={[
                  { label: "Required", value: "required" },
                  { label: "Optional", value: "optional" },
                ]}
              />
            </div>
          </div>
          {selectType && (
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Value set · required</span>
              <ValueSetEditor
                values={options}
                onChange={setOptions}
                duplicateIndexes={duplicateOptionIndexes}
                lockedCount={savedOptionCount}
              />
              {savedOptionCount > 0 && (
                <div className={styles.help}>{SAVED_VALUE_SET_LOCK_MESSAGE}</div>
              )}
              {hasDuplicateOptions && (
                <div className={styles.fieldError} role="alert">
                  {DUPLICATE_VALUE_MESSAGE}
                </div>
              )}
            </div>
          )}
        </ModalV2.Body>
        <ModalV2.Footer>
          <div className={styles.modalFooterSplit}>
            <button
              className={styles.dangerGhost}
              type="button"
              onClick={openDeleteConfirmation}
            >
              <Icon type="trash" size="small" /> Delete attribute
            </button>
            <div className={styles.footerRight}>
              <Button variant="neutral" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                icon="check"
                iconPosition="left"
                disabled={!ok || submitting}
                onClick={() => void submit()}
              >
                Save changes
              </Button>
            </div>
          </div>
        </ModalV2.Footer>
      </ModalV2>

      {confirmDelete && (
        <ModalV2
          show
          // Dismissing mid-flight would let a reopen fire a second delete for
          // the same id before the first settles.
          onHide={() => !deleting && closeDeleteConfirmation()}
          size="medium"
          title={`Delete “${local.name}” from ${space.name}?`}
        >
          <ModalV2.Body>
            <Banner
              variant="warning"
              title="This does not remove it from the data model"
              description="Deleting takes the attribute out of filters, table columns, and the upload form going forward. It is not removed from the underlying attribute data model — documents that already carry it keep their values, and it stays available via APIs and MCP automation."
            />
            {deleteImpact.status === "loading" && (
              <div className={styles.deleteCount}>Checking affected documents…</div>
            )}
            {deleteImpact.status === "ready" && (
              <div className={styles.deleteCount}>
                <strong>{deleteImpact.count}</strong>{" "}
                {deleteImpact.count === 1 ? "document" : "documents"} in {space.name} currently{" "}
                {deleteImpact.count === 1 ? "has" : "have"} a value for{" "}
                <strong>{local.name}</strong>.
              </div>
            )}
            {deleteImpact.status === "not_found" && (
              <div className={styles.deleteCount} role="alert">
                This attribute no longer exists. Close and refresh the list to reconcile.
              </div>
            )}
            {deleteImpact.status === "unavailable" && (
              <div className={styles.deleteCount} role="alert">
                The affected document count is unavailable. Retry before deleting.
                <Button variant="neutral" onClick={() => void loadDeleteImpact()}>
                  Retry
                </Button>
              </div>
            )}
            {deleteFailed && (
              <div className={styles.deleteCount} role="alert">
                The attribute could not be deleted. Try again.
              </div>
            )}
          </ModalV2.Body>
          <ModalV2.Footer>
            <div className={styles.modalFooter}>
              <Button variant="neutral" disabled={deleting} onClick={closeDeleteConfirmation}>
                Cancel
              </Button>
              <Button
                variant="danger"
                disabled={deleteImpact.status !== "ready" || deleting || !onDelete}
                onClick={() => void confirmDeletion()}
              >
                Delete attribute
              </Button>
            </div>
          </ModalV2.Footer>
        </ModalV2>
      )}
    </>
  );
}

/* ---------------- Grid helpers ---------------- */
const GRID_DEFAULT_COL: AgGrid.ColDef = {
  sortable: true,
  resizable: true,
  filter: true,
  flex: 1,
  minWidth: 120,
  menuTabs: ["filterMenuTab", "generalMenuTab"],
};

const SIDE_BAR: AgGrid.SideBarDef = {
  toolPanels: [
    {
      id: "columns",
      labelDefault: "Columns",
      labelKey: "columns",
      iconKey: "columns",
      toolPanel: "agColumnsToolPanel",
    },
    {
      id: "filters",
      labelDefault: "Filters",
      labelKey: "filters",
      iconKey: "filter",
      toolPanel: "agFiltersToolPanel",
    },
  ],
};

/** Row-count footer ("Rows: N"), matching every settings grid in the design. */
const STATUS_BAR = {
  statusPanels: [
    { statusPanel: "agTotalAndFilteredRowCountComponent", align: "left" as const },
  ],
};

type Modal =
  | { mode: "createGlobal" }
  | { mode: "editGlobal"; key: string }
  | { mode: "editLocal"; row: LocalRow }
  | { mode: "promote"; row: LocalRow }
  | null;

/* ---------------- Component ---------------- */
export function DocumentTypeSettings({
  spaces,
  globalLib,
  domains,
  isSteward = false,
  canManageGlobalAttributes = true,
  tab,
  onTabChange,
  onEditSpace,
  onNewSpace,
  createGlobalOpen,
  onCreateGlobalOpenChange,
  onAddGlobalAttr,
  onSaveGlobalAttr,
  onPromoteLocal,
  onPromotePreflight,
  onDeletionImpact,
  onDeleteLocal,
  onSaveLocal,
}: DocumentTypeSettingsProps) {
  const [innerTab, setInnerTab] = useState<SettingsTab>("spaces");
  const [modal, setModal] = useState<Modal>(null);
  const [localMenu, setLocalMenu] = useState<{ row: LocalRow; x: number; y: number } | null>(
    null,
  );
  // Bumped after a successful promotion (DV-671) or space-local edit (DV-674)
  // to purge the space-local grid's server-side cache — the stale row otherwise
  // lingers until a manual refetch, since the grid's datasource fetches
  // `no-cache`.
  const [localRefreshToken, setLocalRefreshToken] = useState(0);

  /** When the shell drives the primary action (via onCreateGlobalOpenChange), the tab's own
      "New space" / "New global attribute" buttons defer to the app header. */
  const shellControlled = !!onCreateGlobalOpenChange;

  /**
   * The Space-local tab stays off until this flag says otherwise. Promote
   * (DV-648) and edit, including the value set (DV-674), are backed now; DELETE
   * still has no mutation behind it, so `onDeleteLocal` is a no-op in the
   * routed shell. Resolved directly rather than threaded in as a prop — same
   * pattern as
   * `useFeatureFlag("showDocumentVault", ...)` in `fund-admin-frontend` and
   * `useFeatureFlag("internationalPaymentRails", ...)` in
   * `jsq-payments-components`'s `BaseWorkflow`.
   */
  const localTabEnabled = useFeatureFlag("documentVaultM2", false);

  const TABS: { key: SettingsTab; label: string; steward?: boolean; enabled?: boolean }[] = [
    { key: "spaces", label: "Spaces" },
    { key: "global", label: "Global attributes" },
    {
      key: "local",
      label: "Space-local attributes",
      steward: true,
      enabled: localTabEnabled,
    },
  ];
  const visibleTabs = TABS.filter((t) => (t.enabled ?? true) && (!t.steward || isSteward));
  const requested = tab ?? innerTab;
  /** A tab the current user can't reach — switched off, or steward-only for a
      non-steward — falls back to Spaces, so a stale deep link still lands somewhere. */
  const activeTab: SettingsTab = visibleTabs.some((t) => t.key === requested)
    ? requested
    : "spaces";
  const setTab = (t: SettingsTab) => (onTabChange ? onTabChange(t) : setInnerTab(t));

  /* Spaces rows */
  const spaceRows = useMemo(
    () =>
      spaces.map((s) => ({
        id: s.id,
        name: s.name,
        domain: domainName(s.domain, domains),
        domId: s.domain,
        attrs: `${globalLib.filter((a) => s.globals[a.key]?.on).length} global · ${s.locals.length} local`,
      })),
    [spaces, domains, globalLib],
  );
  const spaceCols: AgGrid.ColDef<SpaceRow>[] = [
    { headerName: "Space", field: "name", flex: 2, minWidth: 240 },
    { headerName: "Domain", field: "domain", flex: 1, minWidth: 160 },
    {
      headerName: "Attributes",
      field: "attrs",
      flex: 1.2,
      minWidth: 200,
      cellStyle: { color: "#72716e" },
    },
  ];

  /* Global rows */
  const globalRows = useMemo(
    () =>
      sortGlobalsByRolePriority(
        globalLib.map((a) => ({
          key: a.key,
          name: a.name,
          flavor: a.flavor,
          dataType: a.dataType,
          captures: a.captures,
          valueSet: a.values ? a.values.join(" · ") : "—",
          usage: `${spaces.filter((s) => s.globals[a.key]?.on).length} of ${spaces.length} spaces`,
        })),
        globalLib,
      ),
    [globalLib, spaces],
  );
  const globalCols: AgGrid.ColDef<GlobalRow>[] = [
    { headerName: "Attribute", field: "name", flex: 1.4, minWidth: 180 },
    { headerName: "Slug", field: "key", width: 160, cellClass: styles.slugCell },
    {
      headerName: "Source",
      field: "flavor",
      width: 160,
      cellRenderer: SourceCell,
      filterValueGetter: (p) => sourceFilterValue(p.data),
    },
    { headerName: "Data type", field: "dataType", width: 140, cellStyle: { color: "#72716e" } },
    {
      headerName: "Captures",
      field: "captures",
      flex: 2,
      minWidth: 220,
      cellStyle: { color: "#72716e" },
    },
    {
      headerName: "Value set",
      field: "valueSet",
      flex: 1.2,
      minWidth: 150,
      cellStyle: { color: "#72716e" },
    },
    {
      headerName: "Referenced by",
      field: "usage",
      width: 150,
      cellStyle: { color: "#72716e" },
    },
  ];

  /* Modal resolution */
  const editingGlobal =
    modal?.mode === "editGlobal" ? (globalLib.find((a) => a.key === modal.key) ?? null) : null;
  // Space-local rows come from the server (see `SpaceLocalAttributesGrid`), so
  // the modal target can't always be resolved via `spaces.find(...)` — a
  // later page's row may never have been in the `spaces` prop to begin with.
  // Prefer the loaded Space when it IS in `spaces` (the common case: it
  // carries every local, so `useAttributeNameConflict` can see real
  // siblings to collide with) and fall back to the row-only minimal Space
  // otherwise (uniqueness still enforced server-side on save).
  const localTarget =
    modal && (modal.mode === "editLocal" || modal.mode === "promote")
      ? {
          sp: spaces.find((s) => s.id === modal.row.spaceId) ?? spaceFromLocalRow(modal.row),
          loc: modal.row.local,
        }
      : null;

  return (
    <div>
      <div className={styles.tabs} role="tablist">
        {visibleTabs.map((t) => (
          <button
            key={t.key}
            role="tab"
            type="button"
            data-testid={`settings-tab-${t.key}`}
            aria-selected={activeTab === t.key}
            className={activeTab === t.key ? styles.tab.on : styles.tab.off}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "spaces" && (
        <div>
          <div className={styles.infobox}>
            <span className={styles.infoboxIcon}>
              <Icon type="info-circle" size="small" variant="outline" />
            </span>
            <div>
              A <b>space</b> is a bounded context that owns two things: the attributes it
              references and the validation over them. Documents <b>belong to</b> a space —
              there is no type tree and no inheritance.
            </div>
          </div>
          {!shellControlled && onNewSpace && (
            <div className={styles.bar}>
              <span />
              <Button variant="primary" icon="plus" iconPosition="left" onClick={onNewSpace}>
                New space
              </Button>
            </div>
          )}
          <div className={styles.results}>
            <div className={styles.gridWrap}>
              <Grid<SpaceRow>
                rowData={spaceRows}
                columnDefs={spaceCols}
                defaultColDef={GRID_DEFAULT_COL}
                sideBar={SIDE_BAR}
                statusBar={STATUS_BAR}
                onRowClicked={(e) => e.data && onEditSpace?.(e.data.id)}
              />
            </div>
          </div>
        </div>
      )}

      {activeTab === "global" && (
        <div>
          <div className={styles.infobox}>
            <span className={styles.infoboxIcon}>
              <Icon type="info-circle" size="small" variant="outline" />
            </span>
            <div>
              <b>Global attributes</b> are the shared, steward-governed vocabulary available to
              every space. Turning one on in a space references this single definition — its
              data type, requirement, and value set stay consistent everywhere it’s used.
            </div>
          </div>
          {!shellControlled && isSteward && canManageGlobalAttributes && (
            <div className={styles.bar}>
              <span />
              <Button
                variant="primary"
                icon="plus"
                iconPosition="left"
                onClick={() => setModal({ mode: "createGlobal" })}
              >
                New global attribute
              </Button>
            </div>
          )}
          <div className={styles.results}>
            <div className={styles.gridWrap}>
              <Grid<GlobalRow>
                rowData={globalRows}
                columnDefs={globalCols}
                defaultColDef={GRID_DEFAULT_COL}
                sideBar={SIDE_BAR}
                statusBar={STATUS_BAR}
                onRowClicked={(e) =>
                  isSteward &&
                  canManageGlobalAttributes &&
                  e.data &&
                  setModal({ mode: "editGlobal", key: e.data.key })
                }
              />
            </div>
          </div>
        </div>
      )}

      {activeTab === "local" && (
        <div>
          <div className={styles.infobox}>
            <span className={styles.infoboxIcon}>
              <Icon type="info-circle" size="small" variant="outline" />
            </span>
            <div>
              Space-local attributes are created freely by configurators and <b>sandboxed</b> to
              a single space. Click any attribute to edit it, or as steward <b>promote</b> a
              proven one up into the global library so every space can reference it — an
              explicit, confirmed action.
            </div>
          </div>
          <SpaceLocalAttributesGrid
            spaces={spaces}
            domains={domains}
            onRowClicked={(row) => setModal({ mode: "editLocal", row })}
            onActionsClicked={(row, x, y) => setLocalMenu({ row, x, y })}
            refreshToken={localRefreshToken}
          />
        </div>
      )}

      {localMenu &&
        (() => {
          // Only a clash with an existing GLOBAL disables the item — that needs a
          // rename of the global itself, which this flow can't do. Same-named locals
          // in other spaces flow into the promote modal's pre-check conflict list
          // (DV-499), where the steward can rename their way through.
          const clash = findGlobalAttributeNameConflict({
            name: localMenu.row.local.name,
            slug: slugifyAttributeName(localMenu.row.local.name),
            globalAttributes: globalLib,
            localAttributes: [],
            ignoreLocal: {
              spaceId: localMenu.row.spaceId,
              localId: localMenu.row.local.id,
            },
          });
          return (
            <>
              <div
                className={styles.menuBackdrop}
                onClick={() => setLocalMenu(null)}
                aria-hidden
              />
              <div
                className={styles.kebabMenu}
                role="menu"
                style={{ top: localMenu.y + 4, left: Math.max(8, localMenu.x - 224) }}
              >
                <button
                  type="button"
                  role="menuitem"
                  className={styles.kebabItem}
                  onClick={() => {
                    const r = localMenu.row;
                    setLocalMenu(null);
                    setModal({ mode: "editLocal", row: r });
                  }}
                >
                  <Icon type="pen-to-square" size="small" />
                  <span>Edit attribute</span>
                </button>
                {isSteward && canManageGlobalAttributes && (
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.kebabItem}
                    disabled={!!clash}
                    onClick={() => {
                      if (clash) return;
                      const r = localMenu.row;
                      setLocalMenu(null);
                      setModal({ mode: "promote", row: r });
                    }}
                  >
                    <Icon type="shield-alt" size="small" />
                    <span>
                      Promote to global…
                      {clash && (
                        <span className={styles.kebabSub}>
                          “{clash.existingName}” is already {attributeConflictLocation(clash)}
                        </span>
                      )}
                    </span>
                  </button>
                )}
              </div>
            </>
          );
        })()}

      {((shellControlled ? createGlobalOpen : modal?.mode === "createGlobal") ||
        modal?.mode === "editGlobal") && (
        <GlobalAttrModal
          attr={modal?.mode === "editGlobal" ? editingGlobal : null}
          globalLib={globalLib}
          spaces={spaces}
          onCreate={onAddGlobalAttr}
          onSave={onSaveGlobalAttr}
          onClose={() => {
            setModal(null);
            onCreateGlobalOpenChange?.(false);
          }}
        />
      )}

      {localTarget && modal?.mode === "editLocal" && (
        <LocalEditModal
          space={localTarget.sp}
          local={localTarget.loc}
          globalLib={globalLib}
          onDeletionImpact={onDeletionImpact}
          onSave={async (spaceId, localId, patch, saved) => {
            const wrote = await onSaveLocal?.(spaceId, localId, patch, saved);
            // Only on success, and only when something was actually written: an
            // explicit `false` means the diff was empty, so a refetch would burn
            // a round-trip to redisplay identical rows. The grid's datasource
            // fetches `no-cache`, so an Apollo refetch never reaches it —
            // without this the edited row would keep showing its old label and
            // value set until a manual refresh.
            if (wrote !== false) setLocalRefreshToken((t) => t + 1);
          }}
          onDelete={
            onDeleteLocal
              ? async (spaceId, localId, name) => {
                  await onDeleteLocal(spaceId, localId, name);
                  setLocalRefreshToken((t) => t + 1);
                }
              : undefined
          }
          onClose={() => setModal(null)}
        />
      )}

      {localTarget && modal?.mode === "promote" && (
        <PromoteLocalModal
          space={localTarget.sp}
          local={localTarget.loc}
          spaces={spaces}
          onPreflight={onPromotePreflight}
          onPromote={async (newLabel) => {
            await onPromoteLocal?.(localTarget.sp.id, localTarget.loc.id, newLabel);
            // Only on success — a rejection leaves the promoted row in place,
            // so the grid should keep showing it.
            setLocalRefreshToken((t) => t + 1);
          }}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
